import React from 'react';
import { createPortal } from 'react-dom';

export interface DashboardRefreshProgress {
  completed: number;
  total: number;
  label: string;
}

interface DashboardBlockingOverlayProps {
  open: boolean;
  subtitle: string;
  /** Sadece tam dashboard yenilemede; ülke seçimi modunda genelde yok. */
  progress?: DashboardRefreshProgress | null;
}

/**
 * Ülke değişimi veya tam dashboard yenilemesi sırasında tam ekran yükleme.
 * Dashboard’da RetentionTable henüz skeleton iken de (header Yenile) görünmesi için burada tutulur.
 */
export const DashboardBlockingOverlay: React.FC<DashboardBlockingOverlayProps> = ({
  open,
  subtitle,
  progress,
}) => {
  if (!open || typeof document === 'undefined') return null;

  const pct =
    progress && progress.total > 0
      ? Math.min(100, Math.round((progress.completed / progress.total) * 100))
      : 0;
  const remaining = progress ? Math.max(0, progress.total - progress.completed) : 0;

  return createPortal(
    <div
      className="fixed inset-0 z-[2000] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      aria-busy="true"
      aria-label="Veri yükleniyor"
    >
      <div className="flex w-full max-w-md flex-col gap-4 rounded-2xl border border-slate-200 bg-white px-8 py-7 shadow-xl dark:border-slate-600 dark:bg-slate-800">
        <div
          className="mx-auto h-12 w-12 animate-spin rounded-full border-4 border-indigo-200 border-t-indigo-600 dark:border-indigo-900 dark:border-t-indigo-400"
          aria-hidden
        />
        <div className="text-center">
          <p className="text-base font-semibold text-slate-800 dark:text-white">Veriler yükleniyor</p>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">{subtitle}</p>
        </div>

        {progress && progress.total > 0 && (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-xs font-medium text-slate-500 dark:text-slate-400">
              <span>İlerleme</span>
              <span>
                {progress.completed} / {progress.total} adım · %{pct}
                {remaining > 0 ? ` · ~${remaining} adım kaldı` : ''}
              </span>
            </div>
            <div
              className="h-2.5 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700"
              role="progressbar"
              aria-valuenow={pct}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              <div
                className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-violet-500 transition-[width] duration-300 ease-out"
                style={{ width: `${pct}%` }}
              />
            </div>
            <p className="text-center text-xs text-slate-600 dark:text-slate-400">{progress.label}</p>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};
